import { z } from 'zod';
import { Callout } from '../../../__types__/v1/models/Callout';
import { TimestampFilter } from '../../core/standard-date-filter/timestamp-filter';
import {
  BooleanFilter,
  booleanFilter,
  booleanFilterSchema
} from '../_common/query/filters/BooleanFilter';
import {
  StringFilter,
  stringFilter,
  stringFilterSchema
} from '../_common/query/filters/StringFilter';
import { EnumFilter, enumFilter } from '../_common/query/filters/EnumFilter';
import {
  PaginatedResponse,
  paginationSchema,
  paginationUtils
} from '../_common/query/pagination';
import {
  searchUtils,
  withBuilderUtils,
  withFiltersUtils,
  WithOptionalFilters,
  WithPagination,
  withPaginationUtils,
  WithSearch,
  withSearchUtils,
  WithSorter,
  withSorterUtils
} from '../_common/query/queryUtils';
import { Maybe } from '@passionware/monads';
import { coreFieldFiltersSchema } from 'v5/core/core-fields/core-fields-filters.schemas';
import {
  WithWorkspaceScoping,
  withWorkspaceScoping,
  workspaceScopingQueryUtils
} from '../_common/query/workspace-scoping';

export type CalloutStatus = NonNullable<Callout['status']>;

export const CALLOUTS_SORT_FIELDS = [
  'title',
  'slug',
  'createdAt',
  'deadline'
] as const;

export type CalloutsSortField = (typeof CALLOUTS_SORT_FIELDS)[number];

export type CalloutsTableColumnField =
  | CalloutsSortField
  | 'new_applicants'
  | 'status'
  | 'user_id';

export type CalloutsSearchQuery = WithSearch &
  WithPagination &
  WithSorter<CalloutsSortField> &
  WithOptionalFilters<{
    userId: EnumFilter<string>; // owner filter - enum
    archived: BooleanFilter; // boolean filter
    published: BooleanFilter; // boolean filter
    slug: StringFilter;
    createdAt: TimestampFilter; // timestamp filter
  }>;

export type CalloutsQuery = WithWorkspaceScoping<CalloutsSearchQuery>;
export type CalloutsListQuery = CalloutsSearchQuery | CalloutsQuery;

export function asGlobalCalloutsQuery<T extends CalloutsSearchQuery>(
  searchQuery: T
): CalloutsQuery {
  return withWorkspaceScoping(
    searchQuery,
    workspaceScopingQueryUtils.ofGlobal()
  );
}

export type CalloutsListResponse = PaginatedResponse<Callout>;

export interface CalloutAssociations {
  id: Callout['id']; // Added for batch response compatibility
  applicants: Array<{
    id: number;
    callout_id: number;
    contact_id: Maybe<number>;
    contact: Maybe<any>; // null when applicant is "new", populated when "added"
    archived: boolean;
    created_at: string;
    updated_at: string;
    data: Record<string, any>;
    source_ip: Maybe<string>;
  }>;
}

export interface CalloutPublicResponse {
  public_settings: object;
  callout: Callout;
}

export interface TransferCalloutsToWorkspaceRequest {
  ids: Callout['id'][];
  enterprise_workspace_id: number;
}

export interface CalloutsApi {
  getList: (
    query: CalloutsListQuery,
    signal?: AbortSignal
  ) => Promise<CalloutsListResponse>;
  getItem: (id: Callout['id'], signal?: AbortSignal) => Promise<Callout>;
  getAssociations: (
    id: Callout['id'],
    signal?: AbortSignal
  ) => Promise<CalloutAssociations>;
  getBatchAssociations: (
    ids: Callout['id'][],
    signal?: AbortSignal
  ) => Promise<CalloutAssociations[]>;
  getItemPublic: (
    id: Callout['id'],
    query?: object,
    signal?: AbortSignal
  ) => Promise<CalloutPublicResponse>;
  transferCalloutsToWorkspace: (
    request: TransferCalloutsToWorkspaceRequest,
    signal?: AbortSignal
  ) => Promise<void>;
}

export const calloutsQueryUtils = withBuilderUtils({
  ...withSearchUtils<CalloutsSearchQuery>(),
  ...withFiltersUtils<CalloutsSearchQuery>(),
  ...withPaginationUtils<CalloutsSearchQuery>(),
  ...withSorterUtils<CalloutsSearchQuery>(),
  ofEmptySearchQuery: (): CalloutsSearchQuery => ({
    search: '',
    sort: null,
    page: paginationUtils.ofDefault(),
    filters: {}
  }),
  ofDefault: (): CalloutsSearchQuery => {
    return {
      search: '',
      sort: { field: 'createdAt', order: 'desc' },
      page: paginationUtils.ofDefault(),
      filters: {
        archived: { operator: 'equal', value: false }
      }
    };
  },
  matchesQuery: (query: CalloutsSearchQuery, callout: Callout) => {
    if (
      query.search &&
      !searchUtils.matches(query.search, callout.title || '')
    ) {
      return false;
    }
    if (!enumFilter.matches(query.filters.userId, callout.user_id)) {
      return false;
    }
    if (!booleanFilter.matches(query.filters.archived, callout.archived)) {
      return false;
    }
    if (
      !booleanFilter.matches(
        query.filters.published,
        callout.published ?? false
      )
    ) {
      return false;
    }
    if (!stringFilter.matches(query.filters.slug, callout.slug ?? null)) {
      return false;
    }
    // Note: Date filtering is handled server-side, client-side date filtering is complex
    // and not implemented for simplicity

    return true;
  }
}).setInitialQueryFactory(x => x.ofEmptySearchQuery);

const strToNull = (str: unknown) => (str === '' ? null : str);

export const calloutsSearchQuerySchema = z
  .object({
    search: z.preprocess(value => value || '', z.string()).default(''),
    filters: coreFieldFiltersSchema
      .extend({
        published: z.preprocess(strToNull, booleanFilterSchema.nullable()),
        slug: z.preprocess(strToNull, stringFilterSchema.nullable())
      })
      .partial(),
    page: paginationSchema,
    sort: z
      .preprocess(
        strToNull,
        z
          .object({
            field: z.enum(CALLOUTS_SORT_FIELDS),
            order: z.enum(['asc', 'desc'])
          })
          .nullable()
      )
      .default(null)
  })
  .catch(e => {
    console.error('Error parsing callouts search query', e.issues);
    return calloutsQueryUtils.ofEmptySearchQuery();
  });

export const calloutsQuerySchema = calloutsSearchQuerySchema;
